import { IDeliverableFormat } from "@shared/lib/types";
import useUpdateDeliverableFormat from "../lib/hooks/useUpdateDeliverableFormat";
import {
  Card,
  Descriptions,
  Flex,
  List,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography,
} from "antd";
import { Button } from "@shared/ui/Button";
import { DownSquareOutlined, EditOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import FormatForm from "@features/deliverableConfigurator/ui/FormatForm";
import { ToastService } from "@shared/lib/services";

export default function FormatCard({
  deliverableId,
  format,
}: {
  deliverableId: string;
  format: IDeliverableFormat;
}) {
  const {
    updateDeliverableFormat,
    isUpdateDeliverableFormatLoading,
    updateDeliverableFormatError,
    isUpdateDeliverableFormatSuccess,
  } = useUpdateDeliverableFormat();
  const [openModal, setOpenModal] = useState<boolean>(false);

  useEffect(() => {
    if (isUpdateDeliverableFormatSuccess) {
      ToastService.success("Format archived successfully.");
    } else if (updateDeliverableFormatError) {
      ToastService.error(updateDeliverableFormatError?.message);
    }
  }, [updateDeliverableFormatError, isUpdateDeliverableFormatSuccess]);
  const updateFormat = async () => {
    updateDeliverableFormat({
      isArchived: true,
      deliverableConfiguratorId: deliverableId,
      deliverableConfiguratorDeliverableId: format._id || "",
    });
  };
  return (
    <Card
      title={
        <Space>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {format.category || "Unnamed format"}
          </Typography.Title>
          {format.isArchived && (
            <Tag color="yellow" style={{ width: "fit-content" }}>
              Archived
            </Tag>
          )}
        </Space>
      }
      extra={
        <Flex gap={10}>
          <Button
            icon={<EditOutlined />}
            disabled={format.isArchived}
            onClick={() => setOpenModal(true)}
            title="Edit"
          />
          <Popconfirm
            title="Archiving the format"
            description="Are you sure you want to archive this format?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => updateFormat()}
          >
            <Button
              icon={<DownSquareOutlined style={{ color: "red" }} />}
              disabled={format.isArchived}
              loading={isUpdateDeliverableFormatLoading}
              title="Archive"
            />
          </Popconfirm>
        </Flex>
      }
      style={{ width: "100%" }}
    >
      <Descriptions
        key={format._id ?? format.category}
        size="small"
        column={1}
        bordered
        style={{ width: "100%" }}
      >
        <Descriptions.Item
          label="Description"
          styles={{
            content: {
              width: "80%",
            },
          }}
        >
          {format.description || "-"}
        </Descriptions.Item>

        <Descriptions.Item
          label="SubTypes"
          styles={{
            content: {
              width: "80%",
            },
          }}
        >
          <List
            dataSource={format.subTypes || []}
            style={{ width: "100%" }}
            pagination={false}
            renderItem={(item) => (
              <List.Item key={item.name}>
                <List.Item.Meta
                  title={
                    <Typography.Text ellipsis title="Name">
                      Name
                    </Typography.Text>
                  }
                  description={
                    <Typography.Text ellipsis title={item.name}>
                      {item.name}
                    </Typography.Text>
                  }
                />
                <List.Item.Meta
                  title={
                    <Typography.Text ellipsis title="Description">
                      Description
                    </Typography.Text>
                  }
                  description={
                    <Typography.Text ellipsis title={item.description}>
                      {item.description}
                    </Typography.Text>
                  }
                />
                <List.Item.Meta
                  title={
                    <Typography.Text ellipsis title="Values">
                      Values
                    </Typography.Text>
                  }
                  description={
                    <Typography.Text ellipsis title={item.values.join(", ")}>
                      {item.values.join(", ")}
                    </Typography.Text>
                  }
                />
              </List.Item>
            )}
          />
        </Descriptions.Item>
      </Descriptions>

      <Modal
        open={openModal}
        title={"Edit Format"}
        footer={null}
        destroyOnClose
        onCancel={() => setOpenModal(false)}
      >
        <FormatForm
          format={format}
          onClose={() => setOpenModal(false)}
          isEdit={true}
          deliverableId={deliverableId}
        />
      </Modal>
    </Card>
  );
}
